/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import RichTooltip from 'src/components/ts-components/RichTooltip'
import type {
  BalanceSlotDisplayStatus,
  FinderHiddenCause,
} from '../utils/balanceFinder'
import { displayStatusLabel } from '../utils/balanceFinder'
import { balanceText } from '../utils/balanceI18n'

/**
 * Dot hues mirror the RegistrationTable status language (200-tier accents).
 *
 * Exported because the STATUS column's header legend paints the same dots: a
 * legend whose swatches drift from the chips it explains is worse than none.
 */
export const STATUS_DOT_COLORS: Record<BalanceSlotDisplayStatus, string> = {
  open: colors.NEUTRAL_100,
  // D47 — an advertised open slot. Distinct from plain `open` (it is doing
  // something) but not a money state, so it borrows the informational hue.
  player_finder: colors.INFO_200,
  // Listed, but players cannot see the ad: muted like `open`, because no ad is
  // running — the chip must not look as busy as a live one.
  player_finder_hidden: colors.NEUTRAL_100,
  pending: colors.WARNING_200,
  confirmed: colors.INFO_200,
  paid: colors.SUCCESS_200,
  cancelled: colors.ERROR_200,
}

type StatusDotLabelProps = {
  dotColor: string
  label: string
  testId?: string
}

/** Shared dot + label status treatment (matches the registration table). */
export const StatusDotLabel = ({
  dotColor,
  label,
  testId,
}: StatusDotLabelProps) => (
  <span
    css={css`
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      white-space: nowrap;
      color: ${colors.NEUTRAL_30};
    `}
    data-testid={testId}
  >
    <span
      css={css`
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
        background: ${dotColor};
      `}
    />
    {label}
  </span>
)

type BalanceStatusChipProps = {
  /**
   * D47 — the DISPLAY status, not the raw server one. Callers derive it through
   * `slotDisplayStatus()` so the chip, the grid sort and the filter can never
   * disagree about what a row says.
   */
  status: BalanceSlotDisplayStatus
  /**
   * `player_finder_hidden` only — why players cannot see the ad, so the tooltip
   * never blames the recruiting switch for a competition-level or roster-full
   * hide. Absent reads as unexplained.
   */
  hiddenCause?: FinderHiddenCause
  /**
   * Sep 23 — a team official's line: its pending / invoiced / paid tooltips speak of
   * the official's share, not a player's. Everything else about the chip is the same.
   */
  official?: boolean
}

/**
 * The status column is where the sheet is asked "what does this mean?" most
 * often — six one-word states covering a slot's whole lifecycle, three of which
 * (open / PLAYER FINDER / confirmed) are indistinguishable from the label alone.
 *
 * Each state therefore carries two lines: what it MEANS and what happens NEXT.
 * The second half is the one that stops a user opening another screen to find
 * out, so every state has one — including `paid`, whose answer is "nothing".
 */
const STATUS_TIP_KEYS: Record<
  BalanceSlotDisplayStatus,
  { tip: string; next: string }
> = {
  open: {
    tip: 'common:balanceStatusTipOpen',
    next: 'common:balanceStatusNextOpen',
  },
  player_finder: {
    tip: 'common:balanceStatusTipPlayerFinder',
    next: 'common:balanceStatusNextPlayerFinder',
  },
  // Unexplained; the cause-specific copy below replaces it whenever the server
  // says why (HIDDEN_CAUSE_TIP_KEYS).
  player_finder_hidden: {
    tip: 'common:balanceStatusTipPlayerFinderHiddenOther',
    next: 'common:balanceStatusNextPlayerFinderHiddenOther',
  },
  pending: {
    tip: 'common:balanceStatusTipPending',
    next: 'common:balanceStatusNextPending',
  },
  confirmed: {
    tip: 'common:balanceStatusTipConfirmed',
    next: 'common:balanceStatusNextConfirmed',
  },
  paid: {
    tip: 'common:balanceStatusTipPaid',
    next: 'common:balanceStatusNextPaid',
  },
  cancelled: {
    tip: 'common:balanceStatusTipCancelled',
    next: 'common:balanceStatusNextCancelled',
  },
}

/**
 * Sep 23 — the three statuses an official's line reaches, in an official's terms.
 * Their "next" copy is the player's: invoicing, reminding and settling work the same.
 */
const OFFICIAL_TIP_KEYS: Partial<Record<BalanceSlotDisplayStatus, string>> = {
  pending: 'common:balanceStatusTipPendingOfficial',
  confirmed: 'common:balanceStatusTipConfirmedOfficial',
  paid: 'common:balanceStatusTipPaidOfficial',
}

/** Review round 1, TF-03 — a hidden ad's tooltip says WHY, and what brings it back. */
const HIDDEN_CAUSE_TIP_KEYS: Record<
  FinderHiddenCause,
  { tip: string; next: string }
> = {
  finder_disabled: {
    tip: 'common:balanceStatusTipPlayerFinderHiddenFinderDisabled',
    next: 'common:balanceStatusNextPlayerFinderHiddenFinderDisabled',
  },
  registration_private: {
    tip: 'common:balanceStatusTipPlayerFinderHiddenPrivate',
    next: 'common:balanceStatusNextPlayerFinderHiddenPrivate',
  },
  recruiting_off: {
    tip: 'common:balanceStatusTipPlayerFinderHidden',
    next: 'common:balanceStatusNextPlayerFinderHidden',
  },
  roster_full: {
    tip: 'common:balanceStatusTipPlayerFinderHiddenRosterFull',
    next: 'common:balanceStatusNextPlayerFinderHiddenRosterFull',
  },
  other: STATUS_TIP_KEYS.player_finder_hidden,
}

const BalanceStatusChip = ({
  status,
  hiddenCause,
  official = false,
}: BalanceStatusChipProps) => {
  // The tooltip heading is the SAME string the chip shows — a heading that can
  // drift from the label it explains is worse than no heading.
  const label = displayStatusLabel(status)
  const keys =
    status === 'player_finder_hidden' && hiddenCause
      ? HIDDEN_CAUSE_TIP_KEYS[hiddenCause]
      : STATUS_TIP_KEYS[status] ?? STATUS_TIP_KEYS.open

  return (
    <RichTooltip
      title={label}
      body={balanceText(
        (official ? OFFICIAL_TIP_KEYS[status] : undefined) ?? keys.tip
      )}
      next={balanceText(keys.next)}
    >
      <StatusDotLabel
        dotColor={STATUS_DOT_COLORS[status] ?? STATUS_DOT_COLORS.open}
        label={label}
        testId={`balance-status-${status}`}
      />
    </RichTooltip>
  )
}

export default BalanceStatusChip
